import { useCallback, useEffect, useRef, useState } from 'react';
import { preferencesService } from '../services/preferencesService';
import { sortSavedPromptsByOrder } from '../utils/savedPrompts';
import type {
  SavedPrompt,
  SavedPromptCreateRequest,
  SavedPromptUpdateRequest,
} from '../types/preferences';

type PromptCache = Record<string, SavedPrompt[]>;
type PromptLoadErrors = Record<string, string | undefined>;

interface PromptLoadRequest {
  ownerId: string;
  sessionId: string;
  promise: Promise<SavedPrompt[]>;
}

interface ActivePromptSession {
  ownerId: string;
  sessionId: string;
}

interface UseSavedPromptsResult {
  prompts: SavedPrompt[] | null;
  isLoading: boolean;
  loadError: string | null;
  loadSavedPrompts: () => Promise<SavedPrompt[] | null>;
  createSavedPrompt: (request: SavedPromptCreateRequest) => Promise<SavedPrompt>;
  updateSavedPrompt: (promptId: string, request: SavedPromptUpdateRequest) => Promise<SavedPrompt>;
  updateSavedPromptOrder: (promptIds: string[]) => Promise<SavedPrompt[]>;
  deleteSavedPrompt: (promptId: string) => Promise<SavedPrompt>;
}

const hasCachedPrompts = (cache: PromptCache, cacheKey: string): boolean => (
  Object.prototype.hasOwnProperty.call(cache, cacheKey)
);

const updateAllCachedPrompts = (
  promptCache: PromptCache,
  updatePrompts: (cachedPrompts: SavedPrompt[]) => SavedPrompt[],
): PromptCache => (
  Object.fromEntries(
    Object.entries(promptCache).map(([cacheKey, cachedPrompts]) => [
      cacheKey,
      updatePrompts(cachedPrompts),
    ]),
  ) as PromptCache
);

const requireActivePromptSession = (
  authenticatedUserId: string | null,
  sessionId: string | null,
): ActivePromptSession => {
  if (!authenticatedUserId || !sessionId) {
    throw new Error('An active chat session is required to save prompts.');
  }

  return { ownerId: authenticatedUserId, sessionId };
};

const isCurrentPromptSession = (
  activeOwnerId: string | null,
  activeSessionId: string | null,
  promptSession: ActivePromptSession,
): boolean => (
  activeOwnerId === promptSession.ownerId
  && activeSessionId === promptSession.sessionId
);

export const useSavedPrompts = (
  authenticatedUserId: string | null,
  sessionId: string | null,
): UseSavedPromptsResult => {
  const activePromptCacheKey = sessionId || '';
  const [cachedOwnerId, setCachedOwnerId] = useState<string | null>(authenticatedUserId);
  const [promptCache, setPromptCache] = useState<PromptCache>({});
  const [loadErrors, setLoadErrors] = useState<PromptLoadErrors>({});
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  const activeOwnerIdRef = useRef<string | null>(authenticatedUserId);
  const activeSessionIdRef = useRef<string | null>(sessionId);
  const loadRequestsRef = useRef<Map<string, PromptLoadRequest>>(new Map());

  useEffect(() => {
    activeOwnerIdRef.current = authenticatedUserId;
    activeSessionIdRef.current = sessionId;
    loadRequestsRef.current.clear();
    setCachedOwnerId(authenticatedUserId);
    setPromptCache({});
    setLoadErrors({});
    setLoadingSessionId(null);
  }, [authenticatedUserId, sessionId]);

  const loadSavedPrompts = useCallback(async (): Promise<SavedPrompt[] | null> => {
    if (!authenticatedUserId || !sessionId) {
      return null;
    }
    const promptSession = { ownerId: authenticatedUserId, sessionId };

    if (
      cachedOwnerId === authenticatedUserId
      && hasCachedPrompts(promptCache, activePromptCacheKey)
    ) {
      return promptCache[activePromptCacheKey];
    }

    const activeRequest = loadRequestsRef.current.get(activePromptCacheKey);
    if (
      activeRequest?.ownerId === authenticatedUserId
      && activeRequest.sessionId === sessionId
    ) {
      return activeRequest.promise;
    }

    setLoadingSessionId(activePromptCacheKey);
    setLoadErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[activePromptCacheKey];
      return nextErrors;
    });

    const request = preferencesService.listSavedPrompts(sessionId);
    loadRequestsRef.current.set(activePromptCacheKey, {
      ownerId: authenticatedUserId,
      sessionId,
      promise: request,
    });

    try {
      const prompts = await request;
      if (isCurrentPromptSession(
        activeOwnerIdRef.current,
        activeSessionIdRef.current,
        promptSession,
      )) {
        setCachedOwnerId(authenticatedUserId);
        setPromptCache((currentCache) => ({
          ...currentCache,
          [activePromptCacheKey]: prompts,
        }));
      }
      return prompts;
    } catch (error) {
      if (isCurrentPromptSession(
        activeOwnerIdRef.current,
        activeSessionIdRef.current,
        promptSession,
      )) {
        const errorMessage = error instanceof Error ? error.message : 'Unable to load saved prompts.';
        setLoadErrors((currentErrors) => ({
          ...currentErrors,
          [activePromptCacheKey]: errorMessage,
        }));
      }
      throw error;
    } finally {
      if (loadRequestsRef.current.get(activePromptCacheKey)?.promise === request) {
        loadRequestsRef.current.delete(activePromptCacheKey);
      }
      if (isCurrentPromptSession(
        activeOwnerIdRef.current,
        activeSessionIdRef.current,
        promptSession,
      )) {
        setLoadingSessionId((currentSessionId) => (
          currentSessionId === activePromptCacheKey ? null : currentSessionId
        ));
      }
    }
  }, [activePromptCacheKey, authenticatedUserId, cachedOwnerId, promptCache, sessionId]);

  const createSavedPrompt = useCallback(async (
    request: SavedPromptCreateRequest,
  ): Promise<SavedPrompt> => {
    const promptSession = requireActivePromptSession(authenticatedUserId, sessionId);

    const savedPrompt = await preferencesService.createSavedPrompt(promptSession.sessionId, request);
    if (isCurrentPromptSession(
      activeOwnerIdRef.current,
      activeSessionIdRef.current,
      promptSession,
    )) {
      setPromptCache((currentCache) => updateAllCachedPrompts(
        currentCache,
        (cachedPrompts) => [...cachedPrompts, savedPrompt],
      ));
    }
    return savedPrompt;
  }, [authenticatedUserId, sessionId]);

  const updateSavedPrompt = useCallback(async (
    promptId: string,
    request: SavedPromptUpdateRequest,
  ): Promise<SavedPrompt> => {
    const promptSession = requireActivePromptSession(authenticatedUserId, sessionId);

    const savedPrompt = await preferencesService.updateSavedPrompt(
      promptSession.sessionId,
      promptId,
      request,
    );
    if (isCurrentPromptSession(
      activeOwnerIdRef.current,
      activeSessionIdRef.current,
      promptSession,
    )) {
      setPromptCache((currentCache) => updateAllCachedPrompts(
        currentCache,
        (cachedPrompts) => cachedPrompts.map((prompt) => (
          prompt.id === savedPrompt.id ? savedPrompt : prompt
        )),
      ));
    }
    return savedPrompt;
  }, [authenticatedUserId, sessionId]);

  const updateSavedPromptOrder = useCallback(async (promptIds: string[]): Promise<SavedPrompt[]> => {
    const promptSession = requireActivePromptSession(authenticatedUserId, sessionId);

    const orderedPrompts = await preferencesService.updateSavedPromptOrder(
      promptSession.sessionId,
      promptIds,
    );
    if (isCurrentPromptSession(
      activeOwnerIdRef.current,
      activeSessionIdRef.current,
      promptSession,
    )) {
      const promptsById = new Map(orderedPrompts.map((prompt) => [prompt.id, prompt]));
      setPromptCache((currentCache) => updateAllCachedPrompts(
        currentCache,
        (cachedPrompts) => sortSavedPromptsByOrder(cachedPrompts.map((prompt) => (
          promptsById.get(prompt.id) || prompt
        ))),
      ));
    }
    return orderedPrompts;
  }, [authenticatedUserId, sessionId]);

  const deleteSavedPrompt = useCallback(async (promptId: string): Promise<SavedPrompt> => {
    const promptSession = requireActivePromptSession(authenticatedUserId, sessionId);

    const deletedPrompt = await preferencesService.deleteSavedPrompt(
      promptSession.sessionId,
      promptId,
    );
    if (isCurrentPromptSession(
      activeOwnerIdRef.current,
      activeSessionIdRef.current,
      promptSession,
    )) {
      setPromptCache((currentCache) => updateAllCachedPrompts(
        currentCache,
        (cachedPrompts) => cachedPrompts.filter((prompt) => prompt.id !== deletedPrompt.id),
      ));
    }
    return deletedPrompt;
  }, [authenticatedUserId, sessionId]);

  const prompts = (
    cachedOwnerId === authenticatedUserId
    && hasCachedPrompts(promptCache, activePromptCacheKey)
  )
    ? promptCache[activePromptCacheKey]
    : null;

  return {
    prompts,
    isLoading: cachedOwnerId === authenticatedUserId && loadingSessionId === activePromptCacheKey,
    loadError: loadErrors[activePromptCacheKey] || null,
    loadSavedPrompts,
    createSavedPrompt,
    updateSavedPrompt,
    updateSavedPromptOrder,
    deleteSavedPrompt,
  };
};